import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "store";
import { useLoginWithOrderIdAndDOBMutation } from "store/queries/auth";
import { isPatientTokenReadyFor, onPublicTokenChange } from "store/slices/auth";

interface IPatientOrderTokenSubject {
  id?: string;
  externalId?: string | null;
  patient?: { dateOfBirth?: string | Date | null } | null;
}

type AttemptStatus = "idle" | "pending" | "failed";

/**
 * Ensure a patient token exists FOR THIS ORDER, and report when it is usable (CLE-2181).
 *
 * Patient tokens are bound to a single order. `publicToken` is one global slot reused across
 * every order the user visits, so a query that fires on mount sends whatever token is left over
 * from the previous order and gets a 403 PATIENT_TOKEN_ORDER_MISMATCH. Before CLE-2181 the API
 * accepted any validly-signed token for any order, so the stale token silently "worked" and the
 * SPA never had to care.
 *
 * Two failure modes this closes, both observed on dev:
 *   1. Stale token — OrderView for order B while still holding order A's token: 403.
 *   2. No token at all — `useFinixPaymentProvider` on a fresh page load fires with no
 *      Authorization header, gets a 401, and publicApi's 401 handler bounces the user to
 *      /login. (That one predates CLE-2181; gating on `isReady` fixes it too.)
 *
 * Callers should pass `skip: !isReady` to any publicApi query scoped to this order, and use
 * `isPending` (NOT `!isReady`) for any loading state. The distinction matters: an order that can
 * never be authenticated — no DOB on file, or a caller passing an order without `patient` loaded
 * — would otherwise leave a consumer spinning forever. `isBlocked` says "stop waiting, this is
 * as resolved as it gets", so consumers fall back instead of hanging.
 */
export function usePatientOrderToken(order?: IPatientOrderTokenSubject | null) {
  const dispatch = useDispatch();
  const [login] = useLoginWithOrderIdAndDOBMutation();

  const orderId = order?.id;
  const externalId = order?.externalId;
  const dateOfBirth = order?.patient?.dateOfBirth;

  const isReady = useSelector((state: RootState) =>
    isPatientTokenReadyFor(state, orderId)
  );

  // Keyed by order id so switching orders re-mints. `failed` stops an order that genuinely
  // cannot authenticate (no DOB on file) from retrying every render; `pending` prevents a
  // second mint racing the first. Reset to `idle` once a token lands, so that if our 403
  // handler later clears it we mint again rather than sitting unauthenticated forever.
  const attempt = useRef<{ orderId: string | null; status: AttemptStatus }>({
    orderId: null,
    status: "idle",
  });
  // Mirrored into state so consumers re-render when the attempt settles; the ref remains the
  // synchronous guard (a ref alone cannot drive `isPending`).
  const [status, setStatus] = useState<AttemptStatus>("idle");

  // Nothing to mint with: no order, or no date of birth to authenticate against.
  const cannotMint = !orderId || !externalId || !dateOfBirth;

  useEffect(() => {
    if (cannotMint) return;

    if (isReady) {
      attempt.current = { orderId, status: "idle" };
      setStatus("idle");
      return;
    }

    const { current } = attempt;
    if (
      current.orderId === orderId &&
      (current.status === "pending" || current.status === "failed")
    ) {
      return;
    }

    attempt.current = { orderId, status: "pending" };
    setStatus("pending");
    login({
      orderId: externalId,
      dateOfBirth: new Date(dateOfBirth).toISOString(),
    })
      .unwrap()
      .then(({ token }: { token: string }) => {
        dispatch(onPublicTokenChange({ token, orderId }));
      })
      .catch(() => {
        attempt.current = { orderId, status: "failed" };
        setStatus("failed");
      });
  }, [cannotMint, orderId, externalId, dateOfBirth, isReady, login, dispatch]);

  return {
    isReady,
    /** A mint is in flight — safe for a consumer to show a loading state. */
    isPending: !isReady && !cannotMint && status === "pending",
    /** No token is obtainable for this order. Consumers must fall back, not wait. */
    isBlocked: !isReady && (cannotMint || status === "failed"),
  };
}

export default usePatientOrderToken;
